import type { Definition } from 'typescript-json-schema';
import { SchemaTag } from '@sap/ux-specification-types';
import type { DecoratorStep, DecoratorContext } from '../interfaces';
import { metadataKeys } from '../decorators';
import { ConditionEvaluator } from '../condition-evaluator';

/**
 * Hide step - hides properties when their condition evaluates to true.
 * Only hides if the message step did not add any messages to the property.
 */
export class Hide implements DecoratorStep {
    readonly name = 'hide';

    /**
     * Hides properties whose hide condition evaluates to true and have no messages.
     *
     * @param target - The class prototype containing decorator metadata
     * @param definition - The schema definition to modify
     * @param context - Runtime context for condition evaluation
     */
    init(target: object, definition: Definition, context: DecoratorContext): void {
        if (!definition?.properties || !target) {
            return;
        }

        const evaluator = new ConditionEvaluator(context);

        for (const propertyName in definition.properties) {
            const property = definition.properties[propertyName] as Definition;
            const hideConditions = Reflect.getMetadata(metadataKeys.hide, target, propertyName);

            if (hideConditions) {
                // Hide when ANY condition IS met (OR semantics across multiple @hide decorators)
                // But only if no messages were added by the message step
                const hasMessages =
                    Array.isArray(property[SchemaTag.messages]) && property[SchemaTag.messages].length > 0;
                for (const condition of hideConditions) {
                    const { passed } = evaluator.evaluate(condition);
                    if (passed && !hasMessages) {
                        property[SchemaTag.hidden] = true;
                        break;
                    }
                }
            }
        }
    }
}
